import { ref } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { useSessionStorage } from '@vueuse/core'
import { useSnackbar } from '@/composables/useSnackbar'
import { useAuthStore } from './auth'

export const useTapStore = defineStore('tap', () => {
  const taps = ref([])

  const loading = ref(false)

  const latestRefreshTime = ref('-')

  const selectedTap = useSessionStorage('selectedTap', {
    tapId: null,
    courtHouse: null,
    online: false,
    approvalTasks: {
      fromMerging: [],
      fromCorrection: [],
      inCorrection: []
    },
    correctionTasks: [],
    transcriptionTasks: []
  })

  const selectedTapOfflineErrorDialog = ref(false)

  const { openSnackbar } = useSnackbar()

  const fetchTaps = async () => {
    loading.value = true

    const { data } = await axios('/tapClient/listByOU')
    taps.value = data

    loading.value = false
  }

  const setSelectedTap = ({ tapId, courtHouse }) => {
    selectedTap.value.tapId = tapId
    selectedTap.value.courtHouse = courtHouse
    selectedTap.value.online = true
    selectedTap.value.approvalTasks = {
      fromMerging: [],
      fromCorrection: [],
      inCorrection: []
    }
    selectedTap.value.correctionTasks = []
    selectedTap.value.transcriptionTasks = []

    refresh()
  }

  const setSelectedTapOffline = () => {
    // check to open error dialog
    selectedTap.value.online = false
    selectedTapOfflineErrorDialog.value = true
  }

  const fetchApprovalTasks = async () => {
    loading.value = true

    try {
      // get approval tasks
      const responses = await axios.all([
        axios(`/tasks/tap/${selectedTap.value.tapId}/getApprovalTasksFromMerging`),
        axios(`/tasks/tap/${selectedTap.value.tapId}/getApprovalTasksFromCorrection`),
        axios(`/tasks/tap/${selectedTap.value.tapId}/getApprovalTasksInCorrection`)
      ])
      // map responses to appropriate variables
      const [{ data: fromMergingData }, { data: fromCorrectionData }, { data: inCorrectionData }] =
        responses

      // populate state
      selectedTap.value.approvalTasks.fromMerging = fromMergingData
      selectedTap.value.approvalTasks.fromCorrection = fromCorrectionData

      // reset offline flag
      if (selectedTap.value.online === false) {
        selectedTap.value.online = true
        selectedTapOfflineErrorDialog.value = false
        openSnackbar({
          message: 'Το επιλεγμένο Τ.Α.Π. επανασυνδέθηκε',
          color: 'success'
        })
      }
      selectedTap.value.approvalTasks.inCorrection = inCorrectionData
    } catch (error) {
      if (
        error.response?.data?.error === 'TAP_NOT_AVAILABLE' ||
        error.response?.data?.data === 'TAP_NOT_AVAILABLE'
      ) {
        setSelectedTapOffline()
      }
    }

    loading.value = false
  }

  const fetchTranscriptionTask = async () => {
    loading.value = true

    try {
      const { data } = axios(`tasks/tap/${selectedTap.value.tapId}/getTask`)
      loading.value = false
      return data
    } catch (error) {
      if (
        error.response?.data?.error === 'TAP_NOT_AVAILABLE' ||
        error.response?.data?.data === 'TAP_NOT_AVAILABLE'
      ) {
        setSelectedTapOffline()
      }
    }

    loading.value = false
  }

  const fetchTask = async () => {
    loading.value = true

    try {
      const { data } = await axios(`tasks/tap/${selectedTap.value.tapId}/getTask`)
      loading.value = false
      return {
        isSuccess: true,
        data
      }
    } catch (error) {
      loading.value = false
      if (
        error.response?.data?.error === 'TAP_NOT_AVAILABLE' ||
        error.response?.data?.data === 'TAP_NOT_AVAILABLE'
      ) {
        setSelectedTapOffline()
      } else if (error.response?.data?.status === 'NOT_FOUND') {
        return {
          isSuccess: false,
          notFound: true
        }
      }
    }
  }

  const fetchTranscriptionTasks = async () => {
    loading.value = true

    try {
      const { data } = await axios(`tasks/tap/${selectedTap.value.tapId}/getAllTranscriptionTasks`)
      loading.value = false

      selectedTap.value.transcriptionTasks = data

      return {
        isSuccess: true,
        data
      }
    } catch (error) {
      loading.value = false
      if (
        error.response?.data?.error === 'TAP_NOT_AVAILABLE' ||
        error.response?.data?.data === 'TAP_NOT_AVAILABLE'
      ) {
        setSelectedTapOffline()
      } else if (error.response?.data?.status === 'NOT_FOUND') {
        return {
          isSuccess: false,
          notFound: true
        }
      }
    }
  }

  const fetchCorrectionTasks = async () => {
    loading.value = true

    try {
      const { data } = axios(`tasks/tap/${selectedTap.value.tapId}/getAllCorrectionTasks`)
      selectedTap.value.correctionTasks.value = data
    } catch (error) {
      if (
        error.response?.data?.error === 'TAP_NOT_AVAILABLE' ||
        error.response?.data?.data === 'TAP_NOT_AVAILABLE'
      ) {
        setSelectedTapOffline()
      }
    }

    loading.value = false
  }

  const refresh = () => {
    fetchTaps()

    const authStore = useAuthStore()
    if (selectedTap.value.tapId) {
      if (authStore.user.employeeType === 'Grammateas') {
        fetchApprovalTasks()
      } else if (authStore.user.employeeType === 'Apoixografitis') {
        fetchCorrectionTasks()
      }
    }

    const hours = new Date().getHours().toString().padStart(2, '0')
    const minutes = new Date().getMinutes().toString().padStart(2, '0')
    latestRefreshTime.value = `${hours}:${minutes}`
  }

  const reset = () => {
    selectedTap.value = {
      tapId: null,
      courtHouse: null,
      online: false,
      approvalTasks: {
        fromMerging: [],
        fromCorrection: [],
        inCorrection: []
      },
      correctionTasks: [],
      transcriptionTasks: []
    }
  }

  // fetch initial data and continuously fetch taps and selected tap tasks every minute
  refresh()
  setInterval(() => refresh(), 60000)

  return {
    taps,
    loading,
    fetchTaps,
    selectedTap,
    setSelectedTapOffline,
    selectedTapOfflineErrorDialog,
    setSelectedTap,
    fetchApprovalTasks,
    fetchTask,
    fetchTranscriptionTasks,
    fetchTranscriptionTask,
    fetchCorrectionTasks,
    reset,
    refresh,
    latestRefreshTime
  }
})
